export const INSPECTION_SOURCE_IDS = {
  lines: "inspection-lines",
  poles: "inspection-poles",
  selectedLine: "inspection-selected-line",
  selectedPole: "inspection-selected-pole",
};

export const INSPECTION_LAYER_IDS = {
  selectedLine: "inspection-selected-line-layer",
  lines: "inspection-lines-layer",
  linesHit: "inspection-lines-hit-layer",
  polesPulse: "inspection-poles-emergency-pulse",
  poles: "inspection-poles-layer",
  selectedPole: "inspection-selected-pole-layer",
  polesHit: "inspection-poles-hit-layer",
};

const NORMAL_MAP_LAYER_IDS = [
  "line-layer",
  "line-hit-layer",
  "pole-layer",
  "pole-hit-layer",
  "substation-layer",
  "substation-labels",
  "line-end-layer",
  "draft-poles-layer",
  "substation-areas-fill",
  "substation-areas-outline",
];

const EMPTY_COLLECTION = { type: "FeatureCollection", features: [] };

function safeSetLayout(map, layerId, property, value) {
  if (!map?.getLayer?.(layerId)) return;
  try {
    map.setLayoutProperty(layerId, property, value);
  } catch {}
}

export function addInspectionLayers(map) {
  if (!map?.getStyle?.()) return;

  if (!map.getSource(INSPECTION_SOURCE_IDS.lines)) {
    map.addSource(INSPECTION_SOURCE_IDS.lines, {
      type: "geojson",
      data: EMPTY_COLLECTION,
      promoteId: "id",
    });
  }

  if (!map.getSource(INSPECTION_SOURCE_IDS.poles)) {
    map.addSource(INSPECTION_SOURCE_IDS.poles, {
      type: "geojson",
      data: EMPTY_COLLECTION,
      promoteId: "id",
    });
  }

  if (!map.getSource(INSPECTION_SOURCE_IDS.selectedLine)) {
    map.addSource(INSPECTION_SOURCE_IDS.selectedLine, {
      type: "geojson",
      data: EMPTY_COLLECTION,
      promoteId: "id",
    });
  }

  if (!map.getSource(INSPECTION_SOURCE_IDS.selectedPole)) {
    map.addSource(INSPECTION_SOURCE_IDS.selectedPole, {
      type: "geojson",
      data: EMPTY_COLLECTION,
      promoteId: "id",
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.selectedLine)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.selectedLine,
      type: "line",
      source: INSPECTION_SOURCE_IDS.selectedLine,
      layout: { "line-join": "round", "line-cap": "round", visibility: "none" },
      paint: {
        "line-color": "#f8fafc",
        "line-opacity": 0.95,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          5,
          6,
          10,
          10,
          14,
          15,
        ],
        "line-blur": 0.5,
      },
    }, map.getLayer(INSPECTION_LAYER_IDS.lines) ? INSPECTION_LAYER_IDS.lines : undefined);
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.lines)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.lines,
      type: "line",
      source: INSPECTION_SOURCE_IDS.lines,
      layout: { "line-join": "round", "line-cap": "round", visibility: "none" },
      paint: {
        "line-color": [
          "case",
          [">", ["coalesce", ["get", "emergency"], 0], 0],
          "#991b1b",
          [">", ["coalesce", ["get", "problem"], 0], 0],
          "#dc2626",
          ["==", ["get", "status"], "completed"],
          "#16a34a",
          ["==", ["get", "status"], "no_coordinates"],
          "#94a3b8",
          [">=", ["coalesce", ["get", "completionPct"], 0], 80],
          "#84cc16",
          [">", ["coalesce", ["get", "completionPct"], 0], 0],
          "#f97316",
          "#f59e0b",
        ],
        "line-opacity": 0.9,
        "line-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          5,
          2.5,
          10,
          5,
          14,
          8,
        ],
        "line-blur": 0.4,
      },
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.linesHit)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.linesHit,
      type: "line",
      source: INSPECTION_SOURCE_IDS.lines,
      layout: { "line-join": "round", "line-cap": "round", visibility: "none" },
      paint: {
        "line-color": "#000000",
        "line-opacity": 0.01,
        "line-width": ["interpolate", ["linear"], ["zoom"], 5, 14, 10, 24, 14, 34],
      },
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.polesPulse)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.polesPulse,
      type: "circle",
      source: INSPECTION_SOURCE_IDS.poles,
      filter: ["==", ["get", "isEmergency"], true],
      minzoom: 12,
      layout: { visibility: "none" },
      paint: {
        "circle-color": "#ef4444",
        "circle-radius": 13,
        "circle-opacity": 0.28,
        "circle-stroke-color": "#fee2e2",
        "circle-stroke-width": 1,
      },
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.poles)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.poles,
      type: "circle",
      source: INSPECTION_SOURCE_IDS.poles,
      minzoom: 12,
      layout: { visibility: "none" },
      paint: {
        "circle-color": [
          "match",
          ["get", "inspectionStatus"],
          "emergency",
          "#b91c1c",
          "problem",
          "#dc2626",
          "inspected_ok",
          "#16a34a",
          "uninspected",
          "#f59e0b",
          "#94a3b8",
        ],
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, 5, 15, 8, 18, 11],
        "circle-stroke-color": [
          "case",
          ["==", ["get", "inspectionStatus"], "uninspected"],
          "#78350f",
          "#ffffff",
        ],
        "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 12, 1.2, 16, 2],
        "circle-opacity": 0.95,
      },
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.selectedPole)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.selectedPole,
      type: "circle",
      source: INSPECTION_SOURCE_IDS.selectedPole,
      minzoom: 0,
      layout: { visibility: "none" },
      paint: {
        "circle-color": "rgba(255, 255, 255, 0.12)",
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 11, 12, 15, 15, 20, 18, 26],
        "circle-stroke-color": "#f8fafc",
        "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 8, 2, 14, 4, 18, 5],
        "circle-opacity": 1,
      },
    });
  }

  if (!map.getLayer(INSPECTION_LAYER_IDS.polesHit)) {
    map.addLayer({
      id: INSPECTION_LAYER_IDS.polesHit,
      type: "circle",
      source: INSPECTION_SOURCE_IDS.poles,
      minzoom: 12,
      layout: { visibility: "none" },
      paint: {
        "circle-color": "#000000",
        "circle-opacity": 0.01,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, 14, 16, 22, 18, 28],
      },
    });
  }
}

export function setInspectionData(map, { lines, poles }) {
  const lineSource = map?.getSource?.(INSPECTION_SOURCE_IDS.lines);
  const poleSource = map?.getSource?.(INSPECTION_SOURCE_IDS.poles);
  if (lineSource && lines) lineSource.setData(lines);
  if (poleSource && poles) poleSource.setData(poles);
}

export function clearInspectionData(map) {
  setInspectionData(map, { lines: EMPTY_COLLECTION, poles: EMPTY_COLLECTION });
  clearInspectionSelection(map);
}

export function setInspectionSelection(map, { line, pole }) {
  const lineSource = map?.getSource?.(INSPECTION_SOURCE_IDS.selectedLine);
  const poleSource = map?.getSource?.(INSPECTION_SOURCE_IDS.selectedPole);
  if (lineSource) {
    lineSource.setData(line ? { type: "FeatureCollection", features: [line] } : EMPTY_COLLECTION);
  }
  if (poleSource) {
    poleSource.setData(pole ? { type: "FeatureCollection", features: [pole] } : EMPTY_COLLECTION);
  }
}

export function clearInspectionSelection(map) {
  setInspectionSelection(map, { line: null, pole: null });
}

export function setInspectionVisibility(map, visible) {
  const value = visible ? "visible" : "none";
  Object.values(INSPECTION_LAYER_IDS).forEach((id) => safeSetLayout(map, id, "visibility", value));
}

export function setDefaultMapLayersVisibility(map, visible) {
  const value = visible ? "visible" : "none";
  NORMAL_MAP_LAYER_IDS.forEach((id) => safeSetLayout(map, id, "visibility", value));
}

export function startEmergencyPulse(map) {
  let frameId = null;
  const startedAt = performance.now();

  const tick = () => {
    if (!map?.getLayer?.(INSPECTION_LAYER_IDS.polesPulse)) {
      frameId = requestAnimationFrame(tick);
      return;
    }

    const elapsed = performance.now() - startedAt;
    const phase = (Math.sin(elapsed / 420) + 1) / 2;
    try {
      map.setPaintProperty(INSPECTION_LAYER_IDS.polesPulse, "circle-radius", 11 + phase * 9);
      map.setPaintProperty(INSPECTION_LAYER_IDS.polesPulse, "circle-opacity", 0.12 + (1 - phase) * 0.24);
    } catch {}

    frameId = requestAnimationFrame(tick);
  };

  frameId = requestAnimationFrame(tick);
  return () => {
    if (frameId !== null) cancelAnimationFrame(frameId);
  };
}
